import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { getITAsset, getITAssetHistory } from '../../lib/api';
import { ASSET_HISTORY_LABELS, ASSET_STATUSES, assetDisplayName, assetFinancialSummary, assetStatusColor } from '../../components/it-assets';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Desktop, ArrowLeft, Clock, MapPin, User, Wrench, 
  CalendarCheck, CurrencyDollar, Info, Shield
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getITErrorMessage } from '../../lib/itErrors';

export default function ITAssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAsset = useCallback(async () => {
    try {
      setErrorMessage('');
      const [assetRes, historyRes] = await Promise.all([
        getITAsset(assetId),
        getITAssetHistory(assetId).catch(() => ({ data: [] })),
      ]);
      setAsset(assetRes.data);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (err) {
      const message = getITErrorMessage(err, 'Error loading asset');
      setAsset(null);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    loadAsset();
  }, [loadAsset]);

  const statusName = (code) => ASSET_STATUSES.find((status) => status.code === code)?.name || (code || '').replace(/_/g, ' ');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E2B05E]"></div>
        </div>
      </Layout>
    );
  }

  if (errorMessage) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl rounded-2xl border border-[#2A3448] bg-[#151B26] p-6 text-center">
          <h2 className="text-xl font-semibold text-white">No se pudo cargar el activo</h2>
          <p className="mt-2 text-sm text-gray-400">{errorMessage}</p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Button onClick={loadAsset} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">Reintentar</Button>
            <Button variant="outline" onClick={() => navigate('/it/assets')} className="border-gray-700 text-gray-200 hover:bg-[#1E2636]">Volver a activos</Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!asset) return null;

  const specs = asset.specs || {};
  const { financials: fin, totalCost: costoTotal, profit: ganancia, margin: margen } = assetFinancialSummary(asset);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/it/assets')} className="text-gray-400">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Desktop className="text-[#3AB8D6]" size={24} />
              {assetDisplayName(asset)}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
              {asset.serial_number && <span>S/N: {asset.serial_number}</span>}
              {asset.tag && <span>Tag: {asset.tag}</span>}
              {specs.hostname && <span>Host: {specs.hostname}</span>}
            </div>
          </div>
          <Badge className={`${assetStatusColor(asset.status)} text-sm px-3 py-1`}>
            {statusName(asset.status)}
          </Badge>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-[#12141a] border border-gray-700">
            <TabsTrigger value="overview">General</TabsTrigger>
            <TabsTrigger value="technical">Técnico</TabsTrigger>
            <TabsTrigger value="financial">Financiero</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
            <TabsTrigger value="visits">Visitas</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-3 flex items-center gap-2">
                  <Info size={18} /> Identificación
                </h3>
                <div className="space-y-2 text-sm">
                  {[
                    ['Código', asset.asset_code],
                    ['Categoría', (asset.category || '').replace(/[_-]/g, ' ')],
                    ['Subcategoría', specs.subcategory],
                    ['Marca', asset.brand],
                    ['Modelo', asset.model],
                    ['Serial', asset.serial_number],
                    ['Asset Tag', asset.tag],
                    ['Hostname', specs.hostname],
                    ['Código Interno', specs.internal_code],
                    ['Condición', asset.condition],
                    ['Estado Comercial', (specs.commercial_status || '').replace(/_/g, ' ')]
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-400">{label}</span>
                      <span className="text-white capitalize">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-3 flex items-center gap-2">
                  <MapPin size={18} /> Ubicación
                </h3>
                <div className="space-y-2 text-sm">
                  {[
                    ['Cliente', asset.client_name],
                    ['Sucursal', asset.location_name],
                    ['Área', specs.area],
                    ['Ubicación Detallada', specs.detailed_location],
                    ['Usuario Asignado', asset.assigned_to],
                    ['Responsable', specs.client_contact],
                    ['Proveedor', specs.provider]
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-400">{label}</span>
                      <span className="text-white">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
              <h3 className="text-[#E2B05E] font-semibold mb-3 flex items-center gap-2">
                <Shield size={18} /> Garantía y Fechas
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {[
                  ['Fecha Compra', asset.purchase_date],
                  ['Fecha Instalación', specs.install_date],
                  ['Garantía Inicio', specs.warranty_start],
                  ['Garantía Fin', asset.warranty_expiry]
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="text-gray-400 text-xs">{label}</div>
                    <div className="text-white mt-1">{value || '-'}</div>
                  </div>
                ))}
              </div>
            </div>

            {asset.notes && (
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-2">Notas</h3>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{asset.notes}</p>
              </div>
            )}
          </TabsContent>

          {/* Technical Tab */}
          <TabsContent value="technical" className="space-y-4">
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
              <h3 className="text-[#3AB8D6] font-semibold mb-4 flex items-center gap-2">
                <Wrench size={18} /> Especificaciones Técnicas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ['Sistema Operativo', specs.operating_system],
                  ['Procesador', specs.processor],
                  ['RAM', specs.ram],
                  ['Almacenamiento', specs.storage],
                  ['Dirección IP', specs.ip],
                  ['Dirección MAC', specs.mac]
                ].map(([label, value]) => (
                  <div key={label} className="bg-[#12141a] rounded-lg p-3">
                    <div className="text-xs text-gray-500">{label}</div>
                    <div className="text-white text-sm mt-1">{value || '-'}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5 text-center">
                <div className="text-xs text-gray-500 mb-1">Costo Total Real</div>
                <div className="text-2xl font-bold text-red-400">${costoTotal.toFixed(2)}</div>
              </div>
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5 text-center">
                <div className="text-xs text-gray-500 mb-1">Precio al Cliente</div>
                <div className="text-2xl font-bold text-[#3AB8D6]">${(fin.client_price || 0).toFixed(2)}</div>
              </div>
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5 text-center">
                <div className="text-xs text-gray-500 mb-1">Ganancia / Margen</div>
                <div className={`text-2xl font-bold ${ganancia >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  ${ganancia.toFixed(2)} ({margen}%)
                </div>
              </div>
            </div>

            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
              <h3 className="text-[#E2B05E] font-semibold mb-3">Desglose de Costos</h3>
              <div className="space-y-2 text-sm">
                {[
                  ['Costo Unitario', fin.unit_cost],
                  ['Costo Importación', fin.import_cost],
                  ['Costo Instalación', fin.installation_cost],
                  ['Costo Configuración', fin.configuration_cost],
                  ['Costo Logístico', fin.logistics_cost],
                  ['Otros Costos', fin.other_costs],
                  ['Impuestos', fin.taxes]
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-1 border-b border-gray-800/50">
                    <span className="text-gray-400">{label}</span>
                    <span className="text-white">${(Number(value) || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* History Timeline Tab */}
          <TabsContent value="history" className="space-y-4">
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
              <h3 className="text-[#E2B05E] font-semibold mb-4 flex items-center gap-2">
                <Clock size={18} /> Historial del Activo
              </h3>
              {history.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-6">No hay eventos en el historial</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-700"></div>
                  <div className="space-y-4">
                    {history.map((event, idx) => {
                      const meta = ASSET_HISTORY_LABELS[event.action] || { icon: '📝', label: (event.action || '').replace(/_/g, ' ') };
                      const changed = Object.keys(event.changes?.after || {}).filter((key) => key !== 'updated_at');
                      return (
                        <div key={event._id || idx} className="relative pl-10">
                          <div className="absolute left-2 top-1 w-5 h-5 rounded-full bg-[#12141a] border-2 border-[#3AB8D6] flex items-center justify-center text-xs">
                            {meta.icon}
                          </div>
                          <div className="bg-[#12141a] rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-white">{meta.label}</span>
                              <span className="text-xs text-gray-500">
                                {event.performed_at ? new Date(event.performed_at).toLocaleString() : ''}
                              </span>
                            </div>
                            {changed.length > 0 && (
                              <p className="text-sm text-gray-400">Cambios: {changed.join(', ')}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits" className="space-y-4">
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
              <h3 className="text-[#E2B05E] font-semibold mb-4 flex items-center gap-2">
                <CalendarCheck size={18} /> Visitas Asociadas
              </h3>
              {(asset.visit_records || []).length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-6">No hay visitas asociadas a este activo</p>
              ) : (
                <div className="space-y-3">
                  {asset.visit_records.map((record) => (
                    <div
                      key={record._id}
                      onClick={() => record.visit_id && navigate(`/it/visits/${record.visit_id}`)}
                      className="bg-[#12141a] rounded-lg p-4 cursor-pointer hover:bg-[#1e2028] transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium text-sm">
                          {record.visit_info?.visit_number || record.visit_id}
                        </span>
                        <Badge className="bg-gray-500/20 text-gray-300">
                          {(record.visit_info?.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      {record.work_performed && <p className="text-xs text-green-400 mb-1">Trabajo: {record.work_performed}</p>}
                      {record.observations && <p className="text-xs text-gray-400 mb-1">Observaciones: {record.observations}</p>}
                      <div className="text-xs text-gray-500 mt-2">
                        {record.visit_info?.scheduled_for && `Programada: ${record.visit_info.scheduled_for.replace('T', ' ').slice(0, 16)}`}
                        {record.status_before && ` · ${record.status_before} → ${record.status_after || '?'}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
